import React from 'react';
import styled from "styled-components";
import { Link } from 'react-router-dom';
import CoreTemplateTable from '../../../components/CoreTemplateTable';
import FeedDrugTemplateTable from '../../../components/FeedDrugTemplateTable';
import InjectableDrugTemplateTable from '../../../components/InjectableDrugTemplateTable';

const Container = styled.div`
  max-width: 1200px; 
  margin: 0 auto; 
  padding: 1em; 
  color: ${({ theme }) => theme.text};
  background-color: ${({ theme }) => theme.background};
`;

const Section = styled.section`
  margin-bottom: 2em;
`;

const SectionTitle = styled.h4`
  margin-top: 1.5em;
  margin-bottom: 0.5em;
  line-height: 1.3;
  text-align: center;
`;

const Paragraph = styled.p`
  margin-bottom: 0.5em; /* Adjust as needed for spacing between lines */
`;

const List = styled.ul`
  list-style-type: none; /* Remove default bullet points */
  padding-left: 1.5em; /* Indent lists */
  margin-bottom: 1em;
`;

const ListItem = styled.li`
  margin-bottom: 0.2em; 

  a{
  color: ${({ theme }) => theme.headerText};
  }
`;

const NoteTitle = styled.h5` /* Using h5 for notes for semantic hierarchy */
  margin-top: 1.2em;
  margin-bottom: 0.3em;
  font-weight: bold;
`;

const ImportantHighlight = styled.p`
  font-weight: bold;
  color: #c0392b; /* Example: a reddish color for emphasis */
  margin-top: 1em;
  margin-bottom: 1em;
`;

const Image = styled.img`
  max-width: 100%; /* Ensure images don't overflow */
  height: auto; /* Maintain aspect ratio */
  display: block; /* Remove extra space below image */
  margin: 1.5em auto; /* Center images and add vertical spacing */
  border: 1px solid #ddd; /* A subtle border for images */
  box-shadow: 2px 2px 5px rgba(0,0,0,0.1); /* Optional: add a subtle shadow */
`;


const AbhsContent = () => {
  return (
    <Container>
      <Section>
        <SectionTitle>
          Production Lot Upload Data Preparation: Alberta Health Solutions (ABHS)
          <br />
          Site Information Details
        </SectionTitle>
        <Paragraph>Alberta Beef Health Solutions (ABHS)</Paragraph>
        <Paragraph>Vet: CSBV00003 (3)</Paragraph>
        <Paragraph>Project Year: 2021</Paragraph>
        <Paragraph>The site has three (3) herds. They are:</Paragraph>
        <List>
          <ListItem>CSBH000027</ListItem>
          <ListItem>CSBH000028</ListItem>
          <ListItem>CSBH000029</ListItem>
        </List>
        <Paragraph>Access to previous years documentation, click on the appropriate link below:</Paragraph>
        <List>
          
          <ListItem><Link to="/beef/data-preparation/abhs2019">ABHS 2019</Link></ListItem>
          <ListItem><Link to="/beef/data-preparation/abhs2020">ABHS 2020</Link></ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>
          Production Lot Upload Data Preparation: Alberta Health Solutions (ABHS)
          <br />
          Data Source Information
        </SectionTitle>
        <Paragraph>ABHS has 3 herds.</Paragraph>
        <Paragraph>Each spreadsheet/workbook from ABHS represents a specific herd for a specific sampling year.</Paragraph>
        <Paragraph>Within the workbook there is usually just 2 drugs: feed and injectable (parenteral).</Paragraph>
        <Paragraph>The core fields in a sheet have the drug fields at the end of the core field data.</Paragraph>
        <Paragraph>The drug fields are assembled as columns in the spreadsheet.</Paragraph>
        <Paragraph>There are options how to setup each drug.</Paragraph>
        <Paragraph>Each drug sheet can have all associated Core fields and Drug fields together.</Paragraph>
        <Paragraph>Another option is to place all Drug field information, Feed and Injectable, at the end of the Core fields.</Paragraph>
        <Paragraph>Depending on what option is used, the setup of the upload templates will vary.</Paragraph>
        <Paragraph>Before creating and setting up a template, go through all spreadsheets provided from the site. Collect field names from each site and put them together. Review the field names to make sure they all match. My experience is the field names do not match. To reduce the number of templates to create, get the field names to match. The fields do not need to be in the same order.</Paragraph>
        <Paragraph>The other reason to check all spreadsheets before creating a template is to see if there are the same number of drugs.</Paragraph>
        <Paragraph>I have discovered it is the case the # of columns of drugs are not the same or there are more drugs in a spreadsheet compared to another. To make it easier, prepare only 1 template, get all drugs together across all drug routes for all spreadsheet data.</Paragraph>
        <Paragraph>If you do it in this way, it is a possible route to introduce more errors. Be careful!</Paragraph>
      </Section>

      <Section>
        <SectionTitle>
          Production Lot Upload Data Preparation: Alberta Health Solutions (ABHS)
          <br />
          Important Notes for 2020/2021 Data
        </SectionTitle>
        <Paragraph>For project year 2020 it is decided to provide more documentation how to prepare for ABHS data upload. Here are some important notes to consider.</Paragraph>
        <ImportantHighlight>
          ***** Please be reminded *****, ABHS 2021 source changed format from data coming in by columns of data to the same or very similar format to VAHS 2021 data which has core data by columns and the drug data provided in a "by row" format. These changes will be reflected in the template information below.
        </ImportantHighlight>

        <NoteTitle>Note 0:</NoteTitle>
        <List>
          <ListItem>Create worksheet to work on feed called Work-Feed</ListItem>
          <ListItem>Create worksheet to work on parenteral called Work-Injectable</ListItem>
          <ListItem>Copy data over and format appropriately</ListItem>
        </List>

        <NoteTitle>Note 2:</NoteTitle>
        <List>
          <ListItem>Added column Closeout.</ListItem>
          <ListItem>We are missing closeout category in the source data. It sometimes known as season.</ListItem>
          <ListItem>Because of this we will calculate closeout from date in or date out (tba which is correct)</ListItem>
        </List>

        <NoteTitle>Note 3:</NoteTitle>
        <List>
          <ListItem>In Work-Feed worksheet for drug columns we added primary and secondary reasons for use at the end of the drug column name</ListItem>
          <ListItem>Primary reason codes:</ListItem>
          <List>
            <ListItem>GP-Growth promotion</ListItem>
            <ListItem>DP-Disease Prevention</ListItem>
            <ListItem>DT-Disease Treatment</ListItem>
          </List>
          <ListItem>Secondary reasons added from original worksheet called feed.</ListItem>
          <ListItem>These regimen information are in red below the feed source original data.</ListItem>
        </List>

        <NoteTitle>Note 4:</NoteTitle>
        <List>
          <ListItem>Combined the injectable and feed data in worksheet called:</ListItem>
          <ListItem>Work-Injectable & Feed</ListItem>
          <ListItem>We made sure the lot numbers are exactly the same.</ListItem>
        </List>

        <NoteTitle>Note 5:</NoteTitle>
        <List>
          <ListItem>Create a new worksheet</ListItem>
          <ListItem>Call the new worksheet: To Upload-Herd 27 Feed & Inj</ListItem>
          <ListItem>Copy and paste all the data into this worksheet from Work-Injectable & Feed and paste into the new worksheet.</ListItem>
          <ListItem>Herd 27 should be ready to upload.</ListItem>
        </List>

        <NoteTitle>Note 6: Preparing for first upload of raw data into the database</NoteTitle>
        <List>
          <ListItem>Open the database application</ListItem>
          <ListItem>Connect to Development server</ListItem>
          <ListItem>Check if Herd 27 for 2020 is created in the survey.</ListItem>
          <ListItem>If not then add it.</ListItem>
          <ListItem>If you attempt to add a new survey for Herd 27 for year 2020 and no herds are listed, then you have to add allocated herd 27 for 2020</ListItem>
          <ListItem>If you attempt to add a new allocation for Herd 27 for year 2020 and no herds are listed, then you have to go to vet and herd management, find Herd 27 and add project 2020</ListItem>
        </List>

        <NoteTitle>Note 7:</NoteTitle>
        <List>
          <ListItem>Drug name fields should be changed.</ListItem>
          <ListItem>For instance this drug name: tilmicosin-Treatment-Respiratory</ListItem>
          <ListItem>Will be changed to: tilmicosin Treatment Respiratory</ListItem>
          <ListItem>or to: tilmicosin DT Respiratory</ListItem>
          <ListItem>The main thing is a "-" gets converted to null and a "blank" gets converted to _</ListItem>
          <ListItem>Please take note, because it affects the field names in the db uploaded table.</ListItem>
        </List>

        <NoteTitle>Note 8:</NoteTitle>
        <List>
          <ListItem>Some column names in the source spreadsheet are reserved words for PostGreSQL field names.</ListItem>
          <ListItem>Here is the list of names I change so far: (I will expand the list when the need arises)</ListItem>
          <List>
            <ListItem>in &rarr; n_in</ListItem>
            <ListItem>out &rarr; n_out</ListItem>
            <ListItem>type &rarr; type_uk</ListItem>
          </List>
        </List>

        <NoteTitle>Note 9 December 13, 2021: Missing # of animals died</NoteTitle>
        <List>
          <ListItem>Add column into the spreadsheet or calculate using a transform in the template.</ListItem>
          <ListItem>Adding transform for ABHS won't work because we uploaded from Source.</ListItem>
          <ListItem>If template was populated from target then a transform could be done in the template.</ListItem>
          <ListItem>We added 2 columns:</ListItem>
          <List>
            <ListItem># Died &rarr; calculated from # HD - # Head Out</ListItem>
            <ListItem># Railed &rarr; all values set to 0</ListItem>
          </List>
        </List>

        <NoteTitle>Note 10 December 13, 2021: Missing active ingredient for injectable</NoteTitle>
        <List>
          <ListItem>In ABHS 2020 data we are missing these two drugs for injectables in lookup drug table:</ListItem>
          <List>
            <ListItem>chlortetracycline Treatment Lameness</ListItem>
            <ListItem>chlortetracycline Treatment Other</ListItem>
          </List>
          <ListItem>In other words we do not have Chlortetracycline in the active ingredients for injectables.</ListItem>
          <ListItem>I checked if all values are 0 and they are not. The drug needs to be added to the list or some other solution.</ListItem>
        </List>

        <NoteTitle>Note 10A December 14, 2021: Missing active ingredient for injectable</NoteTitle>
        <List>
          <ListItem>I have confirmed with Dr. Sheryl Gow chlortetracycline is probably an Oxytetracycline 200 ml</ListItem>
          <ListItem>But she does not know this. We need to contact vet and confirm.</ListItem>
          <ListItem>Also, I had oxytetracycline injectables to upload but did not know what concentration levels.</ListItem>
          <ListItem>Dr. Sheryl Gow has confirmed through regimen information is Oxy Tet 200</ListItem>
          <ListItem>Update template accordingly.</ListItem>
          <ListItem>Check with Dr. Sheryl Gow for source active ingredient field name</ListItem>
          <List>
            <ListItem>ceftiofur CFA Treatment Lamenessn - code as Ceftiorfor 200</ListItem>
            <ListItem>ceftiofur NA Treatment Enteric - code as Ceftiorfor 50</ListItem>
          </List>
          <ListItem>Please check and check herd 28 and herd 29 if similar problem exists.</ListItem>
        </List>

        <NoteTitle>Note 11 December 13, 2021: Missing total # of head days</NoteTitle>
        <List>
          <ListItem>Add column into the spreadsheet for total # of head days: total days</ListItem>
          <List>
            <ListItem>It is calculated by: =m2-k2</ListItem>
          </List>
          <ListItem>Make sure to format the column as a number then it won't attempt to return a peculiar date</ListItem>
        </List>

        <NoteTitle>Note 12 December 14, 2021: Future Changes</NoteTitle>
        <List>
          <ListItem>The template field table has fields to describe how data is transferrred from source to target.</ListItem>
          <ListItem>I have two fields I would like to the template field table. Here they are:</ListItem>
          <List>
            <ListItem>1. template field setup is complete</ListItem>
            <ListItem>2. template field is required</ListItem>
          </List>
          <ListItem>It be the template field is required should be added to the target field information. We will change it when there is time.</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>
          Production Lot Upload Data Preparation: Alberta Health Solutions (ABHS)
          <br />
          Template Setup Suggestions
        </SectionTitle>
        <Paragraph>A single upload Template can be setup for one or more years batch of data.</Paragraph>
        <Paragraph>For the current document, assume we place all drug known fields for all drug routes at the end of the core fields.</Paragraph>
        <Paragraph>When do this, then one Template is created.</Paragraph>
        <Paragraph>The Template is populated from a worksheet from a specific spreadsheet or workbook.</Paragraph>
        <Paragraph>The reason for this is because the drug fields are in columns not rows.</Paragraph>
        <Paragraph>The system is designed to deal with it but you must upload by Source data to get it to work correctly.</Paragraph>
        <Paragraph>The Template should be setup to have multiple or mix of drugs, data is by column and populated by source.</Paragraph>
        <Paragraph>Here are the typical template settings used for ABHS:</Paragraph>
        <List>
          <ListItem><strong>Name:</strong> ABHS 2021 - Core (73)</ListItem>
          <ListItem><strong>Level:</strong> Top Level (1)</ListItem>
          <ListItem><strong>Type:</strong> Core (1)</ListItem>
          <ListItem><strong>Route:</strong> Multiple Drugs (4)</ListItem>
          <ListItem><strong>Version:</strong> Version 1 (1)</ListItem>
          <ListItem><strong>By Row or Column:</strong> By Column (2)</ListItem>
          <ListItem><strong>Method:</strong> Populate template fields from Target</ListItem>
        </List>
        <Image src="/images/ss_template_abhs_core_2021.png" alt="Template setup for ABHS Core 2021" style={{ maxWidth: '100%', height: 'auto' }} />

        <CoreTemplateTable />

        <br />
        <br /><br />
        <Image src="/images/ss_template_abhs_drug_feed_2021.png" alt="ABHS 2021 Feed Drugs Template" style={{ maxWidth: '100%', height: 'auto' }} />
        <br />
        <br /><br />
        <List>
          <ListItem><strong>Name:</strong> ABHS 2021 - Feed Drugs (75)</ListItem>
          <ListItem><strong>Level:</strong> Second Level (2)</ListItem>
          <ListItem><strong>Type:</strong> Drug (2)</ListItem>
          <ListItem><strong>Route:</strong> Feed (2)</ListItem>
          <ListItem><strong>Version:</strong> Version 1 (1)</ListItem>
          <ListItem><strong>By Row or Column:</strong> By Row (1)</ListItem>
          <ListItem><strong>Method:</strong> Populate template fields from Target</ListItem>
        </List>
        <br /><br />
        <Image src="/images/ss_template_mvs_drug_feed_2021.png" alt="MVS 2021 Feed Drugs Template" style={{ maxWidth: '100%', height: 'auto' }} />

        <FeedDrugTemplateTable />

        <List>
          <ListItem><strong>Name:</strong> ABHS 2021 - Injectable Drugs (74)</ListItem>
          <ListItem><strong>Level:</strong> Second Level (2)</ListItem>
          <ListItem><strong>Type:</strong> Drug (2)</ListItem>
          <ListItem><strong>Route:</strong> Injectable (3)</ListItem>
          <ListItem><strong>Version:</strong> Version 1 (1)</ListItem>
          <ListItem><strong>By Row or Column:</strong> By Row (1)</ListItem>
          <ListItem><strong>Method:</strong> Populate template fields from Target</ListItem>
        </List>

        <Image src="/images/ss_template_mvs_drug_injectable_2021.png" alt='MVS 2021 Injectables Template' style={{ maxWidth: '100%', height: 'auto' }} />

        <InjectableDrugTemplateTable />
      </Section>
    </Container>
  );
};

export default AbhsContent;